import {
	type AnyTenantSeedEvent,
	EventType,
	TENANT_SEED_CONTEXT_PATTERN,
	TENANT_SEED_ERROR_CODE_PATTERN,
	TENANT_SEED_ITEM_COUNT_MAX,
	TENANT_SEED_MESSAGE_MAX,
	TENANT_SEEDER_VERSION_MAX,
	type TenantSeederRegistration,
} from '@today/types'
import { z } from 'zod'

/**
 * The tenant seeding contract (TO-375 REQ-001, REQ-002) — ONE definition shared
 * by every context's seeder (the producer) and the user-service projector (the
 * consumer), so neither can drift from the other.
 */

export const tenantSeedContextSchema = z.string().regex(TENANT_SEED_CONTEXT_PATTERN)
export const seederVersionSchema = z.number().int().min(1).max(TENANT_SEEDER_VERSION_MAX)

const count = z.number().int().min(0).max(TENANT_SEED_ITEM_COUNT_MAX)

export const tenantSeedCompletedDataSchema = z
	.object({
		tenantId: z.uuid(),
		context: tenantSeedContextSchema,
		seederVersion: seederVersionSchema,
		itemsCreated: count,
		itemsSkipped: count,
	})
	.strict()

export const tenantSeedFailedDataSchema = z
	.object({
		tenantId: z.uuid(),
		context: tenantSeedContextSchema,
		seederVersion: seederVersionSchema,
		errorCode: z.string().regex(TENANT_SEED_ERROR_CODE_PATTERN),
		message: z.string().min(1).max(TENANT_SEED_MESSAGE_MAX),
	})
	.strict()

/** The envelope fields a consumer relies on; other envelope keys pass through. */
const wrapper = {
	eventId: z.string().min(1),
	timestamp: z.iso.datetime(),
	tenantId: z.uuid(),
}

const tenantSeedEventSchema = z
	.discriminatedUnion('type', [
		z.object({ ...wrapper, type: z.literal(EventType.TENANT_SEED_COMPLETED), data: tenantSeedCompletedDataSchema }),
		z.object({ ...wrapper, type: z.literal(EventType.TENANT_SEED_FAILED), data: tenantSeedFailedDataSchema }),
	])
	.refine((event) => event.data.tenantId === event.tenantId)

/**
 * Parses an outcome event received from the bus. Returns `null` for anything
 * that is not a well-formed `tenant.seed_completed` / `tenant.seed_failed`,
 * including a `data.tenantId` that differs from the envelope `tenantId`.
 */
export function parseTenantSeedEvent(raw: unknown): AnyTenantSeedEvent | null {
	const parsed = tenantSeedEventSchema.safeParse(raw)
	return parsed.success ? (raw as AnyTenantSeedEvent) : null
}

const registrationSchema = z.object({ context: tenantSeedContextSchema, seederVersion: seederVersionSchema }).strict()

/** REQ-002 — throws when an entry is malformed or a context is registered twice. */
export function assertValidSeederRegistry(entries: readonly TenantSeederRegistration[]): void {
	const seen = new Set<string>()
	for (const entry of entries) {
		const parsed = registrationSchema.safeParse(entry)
		if (!parsed.success) throw new Error(`Invalid seeder registration ${JSON.stringify(entry)}`)
		if (seen.has(entry.context)) throw new Error(`Duplicate seeder registration for context '${entry.context}'`)
		seen.add(entry.context)
	}
}
